import {ReactElement} from 'react'
import {Trans} from 'react-i18next'
import Typography from '@material-ui/core/Typography'
import makeStyles from '@material-ui/core/styles/makeStyles'
import LockOutlined from '@material-ui/icons/LockOutlined'

import messages from '../../../assets/messages'
import {getFormattedAmount} from '../../../common/utils'
import {TauroAccountFactorisation} from '../../../common/utils/accounts'
import {TauroRefundMethodName} from './tauroWithdrawal'

const TAURO_ORANGE = '#F57C00'

const useStyles = makeStyles((theme) => ({
  notice: {
    padding: 16,
    borderRadius: 8,
    border: `1px solid ${TAURO_ORANGE}`,
    backgroundColor: 'rgba(245, 124, 0, 0.08)',
  },
  noticeTitle: {
    fontWeight: 600,
    color: TAURO_ORANGE,
    marginBottom: 4,
  },
  lockedAmount: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 16,
    padding: '12px 16px',
    borderRadius: 8,
    border: `1px solid ${theme.palette.grey[300]}`,
    fontSize: 24,
    fontWeight: 600,
  },
  lockIcon: {
    color: theme.palette.grey[500],
  },
  legs: {
    margin: '16px 0 0',
    paddingInlineStart: 20,
    '& li': {
      marginBottom: 6,
    },
  },
  amount: {
    fontWeight: 600,
  },
  hint: {
    marginTop: 8,
    color: theme.palette.grey[600],
  },
}))

type TauroWithdrawalNoticeProps = {
  factorisation: TauroAccountFactorisation
  currency: string | undefined
  locale: string
}

/**
 * Replaces the amount input for a funded Tauro factorised account (PT-6615): the one-time
 * withdrawal notice, the locked payout, its split into the two legs and the manual review hint.
 */
function TauroWithdrawalNotice(props: TauroWithdrawalNoticeProps): ReactElement {
  const classes = useStyles()
  const {factorisation, currency, locale} = props
  const format = (amount: number | null | undefined) =>
    getFormattedAmount({amount: amount ?? 0, currency, locale})

  return (
    <div data-testid="tauro-withdrawal-notice">
      <div className={classes.notice}>
        <Typography variant="body1" className={classes.noticeTitle}>
          <Trans {...messages.tauroWithdrawalNoticeTitle} />
        </Typography>
        <Typography variant="body2">
          <Trans
            {...messages.tauroWithdrawalNoticeBody}
            values={{multiplier: factorisation.multiplier}}
          />
        </Typography>
      </div>
      <Typography variant="caption" component="div" className={classes.hint}>
        <Trans {...messages.tauroWithdrawalAmount} />
      </Typography>
      <div className={classes.lockedAmount} data-testid="tauro-locked-amount">
        <LockOutlined className={classes.lockIcon} />
        {format(factorisation.clientPayout)}
      </div>
      <ul className={classes.legs}>
        <li data-testid="tauro-refund-leg">
          <Trans
            {...messages.tauroWithdrawalRefundLeg}
            values={{amount: format(factorisation.refundPayout)}}
            components={[<span className={classes.amount}>amount</span>]}
          />{' '}
          <TauroRefundMethodName refundMethod={factorisation.refundMethod} />
        </li>
        {factorisation.profitPayout ? (
          <li data-testid="tauro-profit-leg">
            <Trans
              {...messages.tauroWithdrawalProfitLeg}
              values={{amount: format(factorisation.profitPayout)}}
              components={[<span className={classes.amount}>amount</span>]}
            />
          </li>
        ) : null}
      </ul>
      <Typography variant="body2" className={classes.hint}>
        <Trans {...messages.tauroWithdrawalManualReview} />
      </Typography>
    </div>
  )
}

export {TauroWithdrawalNotice}
